import React from 'react';
import { Button, TableToolbar, TableToolbarContent, TableToolbarSearch } from '@carbon/react';
import { Add, Download } from '@carbon/react/icons';
import { useCrudConfig } from './provider';
import { useMediaQuery } from './useMediaQuery';

export interface CrudToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  createLabel: string;
  onCreate: () => void;
  /**
   * Render the create button. Default true — a page that omits it looks exactly as it did
   * before this prop existed. Set false where the caller has no write access; the server
   * refuses those writes anyway, and a button that always 403s is worse than no button.
   */
  showCreate?: boolean;
  /** The filter chip bar. Rendered on its own row UNDER the toolbar, not inside it. */
  filterBar?: React.ReactNode;
  /**
   * Receives the node the filter menu is portalled into — a counted filter icon sitting
   * immediately after the search, whose popover holds the active chips and `Add filter`.
   */
  onFilterAddSlot?: (node: HTMLElement | null) => void;
  /** The `toolbar` slot: filters a specific page needs, rendered before the create button. */
  children?: React.ReactNode;
  /** The view switcher node, rendered at the start of the toolbar, left of the controls. */
  viewSwitcher?: React.ReactNode;
  /** The column manager node, rendered immediately before the create button. */
  columnManager?: React.ReactNode;
  /**
   * Serialises the current view to CSV. Omit it and no export control renders — which is
   * what a resource declaring `exportable: false` gets.
   */
  onExport?: () => void;
  /** Row count the export would write, shown in the control's name so its scope is plain. */
  exportRowCount?: number;
  /** True when search, sort or filters are active. Drives the Clear button's presence. */
  canClear?: boolean;
  onClear?: () => void;
  /**
   * Rendered INSTEAD of the toolbar's own controls while rows are selected — the filter bar
   * included, which comes back once the selection clears.
   */
  batchBar?: React.ReactNode;
}

export const CrudToolbar = React.memo((props: CrudToolbarProps) => {
  const { translate } = useCrudConfig();
  // Destructured rather than read as `props.onFilterAddSlot` at the ref site: oxlint's
  // react(refs) rule treats the object a ref callback came from as ref-like, and flagged
  // every other `props.*` read in this component as "accessing refs during render".
  const { onFilterAddSlot } = props;
  // Wide screens keep the search open across the row (Carbon's `persistent` field): typing
  // is the most common thing done here, and the icons beside it are all fixed-width. Phones
  // keep the expandable magnifier — next to four 48px icons a persistent field would get
  // ~87px at 375px — and crud.scss overlays it on the row when it opens. 672px is the same
  // breakpoint as crud.scss's `max-width: 671px` phone rules.
  const wide = useMediaQuery('(min-width: 672px)', true);

  return (
    <div className="crud-toolbar">
      {props.batchBar ? (
        <div className="crud-toolbar__batch">{props.batchBar}</div>
      ) : (
        <TableToolbar aria-label={translate('common.dataTableToolbar')}>
          {/* Beside the controls row, not in it: on a phone it takes its own line above them. */}
          {props.viewSwitcher}
          <TableToolbarContent>
            {props.children}
            {/*
              `value` makes this controlled. Without it the `search` prop is decorative: the field
              would not reflect a programmatic clear, a filter reset, or state restored on
              navigation. Verified that Carbon's TableToolbarSearch honours both an initial value
              and an external change to it.
            */}
            <TableToolbarSearch
              persistent={wide}
              value={props.search}
              placeholder={translate('common.search')}
              onChange={(event) => props.onSearchChange((event as React.ChangeEvent<HTMLInputElement>).target.value)}
            />
            {/*
              Portal target for the filter menu (see CrudFilterBar): a single counted icon
              immediately after the search, with the chips themselves inside its popover.
            */}
            {onFilterAddSlot && <div className="crud-toolbar__filter-slot" ref={onFilterAddSlot} />}
            {props.canClear && props.onClear && (
              <Button kind="ghost" onClick={props.onClear}>
                {translate('actions.clear')}
              </Button>
            )}
            {props.onExport && (
              /*
                The count rides the accessible name rather than a visible label: the row is a
                fixed 3rem with four controls that already fill a 375px phone, and "Export"
                alone would leave the scope ambiguous on a paginated list.
              */
              <Button
                kind="ghost"
                size="lg"
                hasIconOnly
                renderIcon={Download}
                iconDescription={translate('actions.exportCsvCount', { count: props.exportRowCount ?? 0 })}
                tooltipPosition="bottom"
                className="crud-export__trigger"
                disabled={!props.exportRowCount}
                onClick={props.onExport}
              />
            )}
            {props.columnManager}
            {props.showCreate !== false && (
              <Button renderIcon={Add} onClick={props.onCreate}>
                {props.createLabel}
              </Button>
            )}
          </TableToolbarContent>
        </TableToolbar>
      )}
      {/*
        The chips live outside TableToolbar on purpose: that element is a fixed 3rem row, so a
        chip tall enough to hold an editor overflowed it and painted on top of the table header.
        Their own wrapping row also lets them read left-to-right at full container width.

        Not while the batch bar is up. It replaces the toolbar and the filter slot with it, and
        a filter bar with no slot falls back to inline chip editors — editing one changes the
        filters, which resets the selection mid-keystroke.
      */}
      {props.filterBar && !props.batchBar && <div className="crud-filter-row">{props.filterBar}</div>}
    </div>
  );
});
CrudToolbar.displayName = 'CrudToolbar';
